import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Separator } from '../components/ui/separator';
import { toast } from 'sonner';
import { User, Briefcase, MapPin, Building2, FileText, Mail, Shield, Phone, Camera } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const UserProfileDialog = ({ open, onClose, viewUserId = null }) => {
  const { user, setUser } = useAuth();
  const [profile, setProfile] = useState({
    display_name: '', title: '', department: '', location: '', bio: '', phone: ''
  });
  const [viewUser, setViewUser] = useState(null);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);
  const isOwnProfile = !viewUserId;

  useEffect(() => {
    if (!open) return;
    if (viewUserId) {
      axios.get(`${API}/users/${viewUserId}`).then(res => setViewUser(res.data.user)).catch(() => {});
    } else {
      axios.get(`${API}/users/me/profile`).then(res => {
        const u = res.data.user;
        setProfile({
          display_name: u.display_name || '',
          title: u.title || '',
          department: u.department || '',
          location: u.location || '',
          bio: u.bio || '',
          phone: u.phone || ''
        });
      }).catch(() => {});
    }
  }, [open, viewUserId]);

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const res = await axios.put(`${API}/users/me/profile`, profile);
      toast.success('Profile updated');
      if (setUser) setUser(res.data.user);
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to update profile');
    } finally {
      setSaving(false);
    }
  };

  const handleAvatarUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await axios.post(`${API}/users/me/avatar`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      toast.success('Profile picture updated');
      if (setUser) setUser(res.data.user);
    } catch (e) {
      toast.error('Failed to upload photo');
    } finally {
      setUploading(false);
    }
  };

  const getAvatarUrl = (u) => {
    if (!u?.avatar) return null;
    return u.avatar.startsWith('/') ? `${process.env.REACT_APP_BACKEND_URL}${u.avatar}` : u.avatar;
  };

  // View-only mode for other users
  if (viewUserId && viewUser) {
    const avatarUrl = getAvatarUrl(viewUser);
    return (
      <Dialog open={open} onOpenChange={onClose}>
        <DialogContent className="max-w-md" data-testid="user-profile-view-dialog">
          <div className="text-center pt-4">
            <div className="w-20 h-20 rounded-full bg-[hsl(var(--primary)/0.15)] flex items-center justify-center text-3xl font-bold text-[hsl(var(--primary))] mx-auto mb-3 overflow-hidden">
              {avatarUrl ? <img src={avatarUrl} alt="" className="w-full h-full object-cover" /> : (viewUser.display_name?.[0] || viewUser.username?.[0] || '?')}
            </div>
            <h3 className="text-lg font-semibold" style={{fontFamily: 'Space Grotesk'}}>{viewUser.display_name || viewUser.username}</h3>
            <p className="text-sm text-muted-foreground">@{viewUser.username}</p>
            {viewUser.system_role === 'admin' && (
              <Badge className="mt-2 bg-[hsl(var(--primary))] text-white border-0">
                <Shield className="w-3 h-3 mr-1" /> Admin
              </Badge>
            )}
          </div>
          <Separator className="my-4" />
          <div className="space-y-3">
            {viewUser.title && (
              <div className="flex items-center gap-3 text-sm">
                <Briefcase className="w-4 h-4 text-muted-foreground flex-shrink-0" />
                <span>{viewUser.title}</span>
              </div>
            )}
            {viewUser.department && (
              <div className="flex items-center gap-3 text-sm">
                <Building2 className="w-4 h-4 text-muted-foreground flex-shrink-0" />
                <span>{viewUser.department}</span>
              </div>
            )}
            {viewUser.location && (
              <div className="flex items-center gap-3 text-sm">
                <MapPin className="w-4 h-4 text-muted-foreground flex-shrink-0" />
                <span>{viewUser.location}</span>
              </div>
            )}
            {viewUser.email && (
              <div className="flex items-center gap-3 text-sm">
                <Mail className="w-4 h-4 text-muted-foreground flex-shrink-0" />
                <span className="text-muted-foreground">{viewUser.email}</span>
              </div>
            )}
            {viewUser.phone && (
              <div className="flex items-center gap-3 text-sm">
                <Phone className="w-4 h-4 text-muted-foreground flex-shrink-0" />
                <span>{viewUser.phone}</span>
              </div>
            )}
            {viewUser.bio && (
              <div className="pt-2">
                <p className="text-sm text-muted-foreground">{viewUser.bio}</p>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  // Edit mode for own profile
  const avatarUrl = getAvatarUrl(user);
  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-md" data-testid="user-profile-edit-dialog">
        <DialogHeader>
          <DialogTitle style={{fontFamily: 'Space Grotesk'}}>Edit Profile</DialogTitle>
          <DialogDescription>Update your profile information visible to teammates.</DialogDescription>
        </DialogHeader>

        {/* Avatar upload */}
        <div className="flex items-center gap-4 mb-2">
          <div className="relative group">
            <div className="w-16 h-16 rounded-full bg-[hsl(var(--primary)/0.15)] flex items-center justify-center text-2xl font-bold text-[hsl(var(--primary))] overflow-hidden">
              {avatarUrl ? <img src={avatarUrl} alt="" className="w-full h-full object-cover" /> : (user?.display_name?.[0] || user?.username?.[0] || '?')}
            </div>
            <button
              type="button"
              className="absolute inset-0 rounded-full bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center cursor-pointer"
              onClick={() => fileInputRef.current?.click()}
              data-testid="avatar-upload-button"
            >
              <Camera className="w-5 h-5 text-white" />
            </button>
            <input ref={fileInputRef} type="file" accept="image/*" className="hidden" onChange={handleAvatarUpload} />
          </div>
          <div>
            <p className="text-sm font-medium">{user?.display_name || user?.username}</p>
            <p className="text-xs text-muted-foreground">{user?.email}</p>
            {uploading && <p className="text-xs text-[hsl(var(--primary))]">Uploading...</p>}
          </div>
        </div>

        <form onSubmit={handleSave} className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label className="text-xs flex items-center gap-1"><User className="w-3 h-3" /> Display Name</Label>
              <Input className="h-8 text-sm" value={profile.display_name} onChange={(e) => setProfile({...profile, display_name: e.target.value})} data-testid="profile-display-name" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs flex items-center gap-1"><Briefcase className="w-3 h-3" /> Title</Label>
              <Input className="h-8 text-sm" placeholder="e.g. Senior Engineer" value={profile.title} onChange={(e) => setProfile({...profile, title: e.target.value})} data-testid="profile-title" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label className="text-xs flex items-center gap-1"><Building2 className="w-3 h-3" /> Department</Label>
              <Input className="h-8 text-sm" placeholder="e.g. Engineering" value={profile.department} onChange={(e) => setProfile({...profile, department: e.target.value})} data-testid="profile-department" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs flex items-center gap-1"><MapPin className="w-3 h-3" /> Location</Label>
              <Input className="h-8 text-sm" placeholder="e.g. San Francisco" value={profile.location} onChange={(e) => setProfile({...profile, location: e.target.value})} data-testid="profile-location" />
            </div>
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs flex items-center gap-1"><Phone className="w-3 h-3" /> Phone Number</Label>
            <Input className="h-8 text-sm" placeholder="+1 (555) 000-0000" value={profile.phone} onChange={(e) => setProfile({...profile, phone: e.target.value})} data-testid="profile-phone" />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs flex items-center gap-1"><FileText className="w-3 h-3" /> Bio</Label>
            <Textarea className="text-sm" placeholder="Tell your team about yourself..." value={profile.bio} onChange={(e) => setProfile({...profile, bio: e.target.value})} rows={2} data-testid="profile-bio" />
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" size="sm" onClick={onClose}>Cancel</Button>
            <Button type="submit" size="sm" disabled={saving} data-testid="profile-save-button">
              {saving ? 'Saving...' : 'Save Changes'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default UserProfileDialog;